import React, { useState, useRef, useEffect } from 'react';
import { Navbar, Nav, Container, Button, Card, Row, Col } from 'react-bootstrap';
import { useAllEmailsQuery,
         useAllImagesQuery,
         useAddEmailMutation,
         useEditEmailMutation,
         useGenerateEmailMutation,
         useLazyGenerateHTMLQuery,
         useDeleteHTMLMutation,
         useSendEmailMutation,
        useLazyJsonDownloadQuery } from '../../app/api';
import EmailPreview from './EmailPreview';
import ChamailleonSettings from '../components/ChamaileonSettings'
import ImageGallery from '../components/ImageGallery'
import TemplateVariableUpload from '../components/TemplateVariableUpload'
import PropTypes from "prop-types";


// Editor Component
const EmailEditor = ({plugins, triggerChild}) => {
    // get data
    const {data: images} = useAllImagesQuery();
    const {data: emails, refetch: refetch_emails} = useAllEmailsQuery();

    // debug
    useEffect(()=>{
        if(emails){
            console.log('EMAILS IN DB: ', emails);
        }
    }, [emails])

    // refresh emails if json was uploaded from sidebar
    
    useEffect(()=>{
        if(triggerChild > 0){ 
            refetch_emails();
        }
    }, [triggerChild])

    // add and edit email mutations
    const [addEmail] = useAddEmailMutation();
    const [editEmail] = useEditEmailMutation();
    const [genEmail] = useGenerateEmailMutation();
    const [sendEmail] = useSendEmailMutation();
    const [triggerGenerateHTML] = useLazyGenerateHTMLQuery();
    const [triggerJsonDownload] = useLazyJsonDownloadQuery();
    const [delEmail] = useDeleteHTMLMutation();
 
    // load chamailleon plugins from props
    const [chamaileonPlugins, setChamaileonPlugins] = useState(plugins);
    useEffect(()=>{
        setChamaileonPlugins(plugins);
    }, [plugins])

    // react useState setters
    const [documentJson, setDocumentJson] = useState(null);
    const [favoriteBlocks, setFavoriteBlocks] = useState([]);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [isTVUOpen, setIsTVUOpen] = useState(false);
    const [isTVU_send_Open, setIsTVU_send_Open] = useState(false);
    const currentTemplateID = useRef(null);
    const currentTemplateTitle = useRef(null);

    const closePreview = () =>{
        setShowPreview(false);
    }

    const handleTemplateVariableUpload_submit = async (json) => {
        let x = await genEmail({json_variables: JSON.stringify(json), id: currentTemplateID.current});
        const blob = new Blob([x.data], { type: 'text/html' });
        // Create a URL for the Blob
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        // Clean up the URL object when done (important to prevent memory leaks)
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    const handleTemplateVariableUploadSEND_submit = async (data) => {
        let x = await sendEmail({
            email_id: currentTemplateID.current,
            recipient: data.email,
            json_variables: JSON.stringify(data.parsedJson)
        });
        console.log('Email Send Response: ', x);
        alert('Email Send Attempted! Email sent to '+data.email);
    }

    const handleDownloadHTMLEmail = async () => {
        let x = await triggerGenerateHTML({id: currentTemplateID.current});
        if(x.isSuccess){
            // download to browser
            const blob = new Blob([x.data], { type: "text/html" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            // create file name
            const now = new Date();
            const timestamp = now.getTime();
            const title = currentTemplateTitle.current.replace(/[^a-zA-Z0-9]/g, '');
            a.download = title+"_"+timestamp+".html";
            a.click();
            URL.revokeObjectURL(url);
        } else {
            alert('There was an error generating your html download. Please reload the dashboard and try again or contact an admin.')
        }
    }

    const handleJSONdownload = async () => {
        let x = await triggerJsonDownload({id: currentTemplateID.current});
        if(x.isSuccess){
            // Create a Blob from the JSON string
            const blob = new Blob([x.data.json_body], { type: 'application/json' });
            // Create a URL for the blob
            const url = URL.createObjectURL(blob);
            // Create a temporary anchor element
            const a = document.createElement('a');
            a.href = url;
            const tnow = new Date();
            a.download = `template_${currentTemplateTitle.current}_${tnow.getTime()}.json`; // Set filename
            // Append to body, click to download, then remove
            document.body.appendChild(a);
            a.click();
            // Clean up
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } else {
            alert('There was an error downloading the json template. Try again or contact an admin.')
        }
    }

    const handleDeleteEmail = async (callback) => {
        if (confirm("Are you sure you want to PERMANENTLY delete this email template?")) {
            let x = await delEmail({id: currentTemplateID.current});
            // console.log('DELETE EMAIL RESPONSE:', x);
            if(x.error && x.error.status == '403'){
                alert('You do not have the required permissions to complete this action.');
            }
            callback();
        }
    }

    const handleEmailSelect = async (emailObj) => {
        let parsedJson = {};
        // editing an existing email
        if(emailObj){
            try {
                parsedJson = JSON.parse(emailObj.json_body);  
                setDocumentJson(parsedJson);
                currentTemplateID.current = emailObj.id;
                currentTemplateTitle.current = emailObj.title;
                let instance = await initializeEditor(parsedJson, emailObj.id);
                await instance.methods.updateData({ document: parsedJson });
                instance.show();
            } catch (error) {
                console.error('Error parsing email JSON stored in database:', error);
            }
        // new email
        } else {
            let instance = await initializeEditor(0, 0);
            instance.show();
        }
    };

    const handleEmailPreview = (emailJson) =>{
        try {
            const parsedJson = JSON.parse(emailJson);
            setDocumentJson(parsedJson);
            setShowPreview(true);
        } catch (error) {
            console.error('Error parsing email JSON:', error);
        }
    }

    const refreshEmails = async () => {
        await refetch_emails();
    }

    const initializeEditor = async (parsedJson, id) => {
        if (!chamaileonPlugins) return;

        let emailBlocks = [];
        if(parsedJson && id){
            emailBlocks = parsedJson.body.children.map((block, idx) => ({
                _id: idx,
                title: `Block ${idx + 1}`,
                body: block
            }));
        }

        // icons for editor: https://pictogrammers.com/library/mdi/
        const instance = await chamaileonPlugins.createFullscreenPlugin({
            plugin: 'editor',
            data: { document: parsedJson ?? null },
            settings: {
                buttons: {
                    header: [
                        {
                            id: 'preview',
                            type: 'button',
                            icon: 'eye',
                            label: 'Preview Email',
                            color: 'primary',
                            style: 'outlined'
                        },
                        {
                            id: 'send-email',
                            type: 'button',
                            icon: 'send',
                            label: 'Send Email',
                            color: 'secondary',
                            style: 'outlined'
                        },
                        {
                            label: 'Actions',
                            icon: 'at',
                            color: 'primary',
                            style: 'outlined',
                            badge: false,
                            items: [
                                {
                                    id: "download-html-email",
                                    label: "Download Email HTML",
                                    icon: "download-box"
                                },
                                {
                                    id: "create-sample-email",
                                    label: "Create Sample HTML Email from JSON",
                                    icon: "email"
                                },
                                {
                                    id: "download-json-email",
                                    label: "Download Chamaileon JSON",
                                    icon: "code-braces"
                                },
                                {
                                    id: "delete-email",
                                    label: "Delete Email",
                                    icon: "delete-forever"
                                }
                            ]
                        }
                    ]
                },
                elements: {
                    content: {
                        text: true,
                        image: true,
                        button: true,
                        social: true,
                        divider: true,
                        code: true
                    },
                    structure: {
                        box: true,
                        multiColumn: true
                    },
                    advanced: {
                        loop: true,
                        conditional: true,
                        dynamicImage: true
                    }
                },
                blockLibraries: [
                    {
                        id: 'email-blocks',
                        label: 'Email\'s blocks',
                        canDeleteBlock: false,
                        canRenameBlock: false,
                        canSaveBlock: false
                    },
                    {
                        id: 'favorite-blocks',
                        label: 'Favorite blocks',
                        canDeleteBlock: true,
                        canRenameBlock: true,
                        canSaveBlock: true
                    }
                ],
                addons: {
                    blockLock: {
                        enabled: true,
                    },
                    variableSystem: {
                        enabled: true
                    },
                },
            },
            hooks: {
                onSave: ({ document }) => {
                    // check if saving an existing email or creating a new one based on the id
                    // id = 0 means creating a new email
                    if(id == 0){
                        addEmail({json_body: JSON.stringify(document), title: document.title})
                        .then(res => {
                            if(res.error && res.error.status == '403'){
                                alert('You do not have the required permissions to complete this action.');
                            }
                        });
                    } else {
                        let x = {
                            email_id: id,
                            title: document.title,
                            json_body: JSON.stringify(document)
                        };
                        editEmail(x).then(res => {
                            if(res.error && res.error.status == '403'){
                                alert('You do not have the required permissions to complete this action.');
                            }
                        });
                        setDocumentJson(document);
                    }
                },
                onLoadBlocks: async ({ libId }) => {
                    if (libId === 'email-blocks') {
                        return { blocks: emailBlocks };
                    }
                    return { blocks: favoriteBlocks };
                },
                onBlockSave: async ({ libId, block }) => {
                    if (libId === 'email-blocks') {
                        throw new Error('It is not allowed to save into this block library');
                    }
                    const newFavoriteBlocks = [...favoriteBlocks, block];
                    setFavoriteBlocks(newFavoriteBlocks);
                    return { block };
                },
                onBlockRename: async ({ libId, block: { _id, title }}) => {
                    if (libId === 'email-blocks') {
                        throw new Error('You are not allowed to rename blocks in this block library.');
                    }
                    const updatedBlocks = favoriteBlocks.map(block => 
                        block._id === _id ? { ...block, title } : block
                    );
                    setFavoriteBlocks(updatedBlocks);
                },
                onBlockDelete: async ({ libId, block: { _id }}) => {
                    if (libId === 'email-blocks') {
                        throw new Error('You are not allowed to delete from this block library.');
                    }
                    const updatedBlocks = favoriteBlocks.filter(block => block._id !== _id);
                    setFavoriteBlocks(updatedBlocks);
                },
                onHeaderButtonClicked: ({ buttonId }) => {
                    if(buttonId == 'create-sample-email'){
                        setIsTVUOpen(true);
                    }
                    if(buttonId == 'preview'){
                        setShowPreview(true);
                    }
                    if(buttonId == 'download-html-email'){
                        handleDownloadHTMLEmail()
                    }
                    if(buttonId == 'send-email'){
                        setIsTVU_send_Open(true);
                    }
                    if(buttonId == 'download-json-email'){
                        handleJSONdownload()
                    }
                    if(buttonId == 'delete-email'){
                        handleDeleteEmail(()=>{
                            instance.hide();
                            instance.destroy();
                            refreshEmails();
                        });
                    }
                },
                onEditImage: async(params) => {
                    console.log('you clicked onEditImage',params);
                    const imageSrc = await showGallery();
                    // this has to return the src for the image.
                    return {src: imageSrc}
                },
                onEditTitle: async(params) => {
                    setDocumentJson({...documentJson, title: params.title});
                },
                close: () => {
                    instance.hide();
                    instance.destroy();
                    refreshEmails();
                }
            }
        });

        // setEditorInstance(instance);
        return instance;
    };

    const handleSettingsSave = async () => {
        try {
            setChamaileonPlugins(plugins);
            setIsSettingsOpen(false);
        } catch (error) {
            console.error('Error saving settings:', error);
            alert('Authentication problem. Please check your API key settings.');
        }
    };

    const showGallery = async () => {
        const galleryModal = document.getElementById('gallery-modal');
        galleryModal.style.zIndex = 20000;
        galleryModal.classList.remove('hidden');

        function closeGalleryModal() {
            galleryModal.classList.add('hidden');
            setTimeout(() => {
                galleryModal.style.zIndex = -1;
            }, 500);
        }
        
        return new Promise((resolve) => {
            document.getElementById('gallery-modal-close').addEventListener('click', function(){
                // resolve({src:null})
                closeGalleryModal();
            });
            if(images){
                images.map(i => {
                    // console.log('adding listener to ', i.filename);
                    document.getElementById(i.filename).addEventListener('click', function(){
                        // console.log('resolving:', 'https://ffn-email-design.s3.us-west-2.amazonaws.com/' + i.filename);
                        resolve('https://ffn-email-design.s3.us-west-2.amazonaws.com/' + i.filename)
                        closeGalleryModal();
                    }) 
                })
            }
        })
    }


    return (
        <div className="email-editor">
            <Navbar bg="light" expand="lg">
                <Container>
                    <Navbar.Brand href="#">
                        <div className="logo-wrapper">
                            <span className="logo-text">FFN Chamaileon POC</span>
                        </div>
                    </Navbar.Brand>
                    <Nav className="ms-auto hidden">
                        <Button 
                            variant="outline-primary" 
                            onClick={() => setIsSettingsOpen(true)} >
                            Settings
                        </Button>
                    </Nav>
                </Container>
            </Navbar>

            <Container className="mt-4">
                <Row>
                    <Col>
                        <Card>
                            <Card.Header><h4>Email Editor</h4></Card.Header>
                            <Card.Body>
                                <p>Choose the email you would like to edit</p>

                                {/* create new email */}
                                <div className="mb-3">
                                    <Button 
                                        variant="primary"
                                        onClick={() => handleEmailSelect(false)}
                                        disabled={!chamaileonPlugins}>
                                        Create New 
                                    </Button>
                                </div>

                                {/* email editing and preview */}
                                {emails && emails.map(e => (
                                    <div key={e.id} className="mb-3">
                                        <Button 
                                            variant="primary"
                                            onClick={() => handleEmailSelect(e)}
                                            disabled={!chamaileonPlugins}>
                                            {e.id}: {e.title} 
                                        </Button>
                                        <Button variant="link"
                                                onClick={()=> handleEmailPreview(e.json_body)}>
                                            Preview
                                        </Button>
                                    </div>
                                ))}
                            </Card.Body>
                        </Card>

                        {/* image gallery */}
                        <ImageGallery/>

                        {/* preview container */}
                        <div id='previewContainer'>
                            {/* creates visible email blog with variables changed */}
                            <TemplateVariableUpload
                                show={isTVUOpen}
                                onHide={() => setIsTVUOpen(false)}
                                onSubmit={handleTemplateVariableUpload_submit}
                            />
                            {/* attempts to send an email with variables replaced */}
                            <TemplateVariableUpload
                                show={isTVU_send_Open}
                                showEmailField={true}
                                onHide={() => setIsTVU_send_Open(false)}
                                onSubmit={handleTemplateVariableUploadSEND_submit}
                            />
                            {showPreview && 
                                <EmailPreview documentJson={documentJson}
                                              chamaileonPlugins={chamaileonPlugins} 
                                              callback={closePreview}/>
                            }
                            {/* {showInlinePreview && chamaileonPlugins && documentJson && 
                                <EmailPreview documentJson={documentJson}
                                              chamaileonPlugins={chamaileonPlugins} 
                                              callback={closePreview}
                                              inline={false}
                                              inlineString={'previewScreen'}/>
                            } */}
                        </div>
                    </Col>
                </Row>
            </Container>

            <ChamailleonSettings 
                isOpen={isSettingsOpen}
                onSave={handleSettingsSave}
                onClose={() => setIsSettingsOpen(false)}
            />
        </div>
    );
};

EmailEditor.propTypes = {
    plugins: PropTypes.object,
    triggerChild: PropTypes.number
}

export default EmailEditor